import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Typography,
  Box,
  Tabs,
  Tab,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Avatar,
  InputAdornment,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as EditIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  AccessTime as LateMarkIcon,
  EventNote as LeaveIcon,
  Schedule as ShiftIcon,
  LocationOn as WorkAreaIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  PendingActions as PendingIcon,
  CheckCircleOutline as ApprovedIcon,
  CancelOutlined as RejectedIcon,
  GroupWork as ShiftAssignedIcon,
  PersonOff as UnassignedIcon,
  Today as TodayIcon,
  SwapHoriz as ConvertedIcon,
  Add as AddIcon,
  PhotoCamera as PhotoCameraIcon,
  PhotoLibrary as PhotoLibraryIcon
} from '@mui/icons-material';
import Layout from '../components/Layout';
import attendanceService from '../services/attendanceService';
import employeeService from '../services/employeeService';
import apiClient from '../services/api';
import '../styles/EmployeeManagement.css';

const apiBase = apiClient.defaults.baseURL || 'http://localhost:5000/api';
const serverUrl = apiBase.endsWith('/api') ? apiBase.slice(0, -4) : apiBase;

// Tab Panel component
function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`employee-tabpanel-${index}`}
      aria-labelledby={`employee-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EmployeeManagementPage = () => {
  // Main tab state (0: Employee List, 1: Late Mark Reporting, 2: Leave Approval, 3: Shift Management, 4: Work Areas)
  const [mainTab, setMainTab] = useState(0);
  
  // Employee List State
  const [employees, setEmployees] = useState([]);
  
  // Late Mark Reporting State
  const [lateMarks, setLateMarks] = useState([]);
  const [lateMarkStats, setLateMarkStats] = useState({
    total: 0,
    thisMonth: 0,
    converted: 0,
    pending: 0
  });
  
  // Leave Approval State
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [leaveBalances, setLeaveBalances] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });
  const [leaveSubTab, setLeaveSubTab] = useState(0);
  
  // Shift Management State
  const [employees_shifts, setEmployees_shifts] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shiftStats, setShiftStats] = useState({ totalEmployees: 0, assigned: 0, unassigned: 0 });
  const [shiftDialogOpen, setShiftDialogOpen] = useState(false);
  const [shiftDialogEmployee, setShiftDialogEmployee] = useState(null);
  const [shiftDialogShiftId, setShiftDialogShiftId] = useState('');
  const [shiftDialogDate, setShiftDialogDate] = useState(new Date().toISOString().split('T')[0]);

  // View Employee Dialog State
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewEmployee, setViewEmployee] = useState(null);

  // Add/Edit Employee Dialog State
  const [addEmployeeDialogOpen, setAddEmployeeDialogOpen] = useState(false);
  const [addEmployeeFormData, setAddEmployeeFormData] = useState({
    employee_id: '',
    first_name: '',
    last_name: '',
    email: '',
    username: '',
    password: '',
    phone: '',
    role: '',
    department: '',
    joining_date: new Date().toISOString().split('T')[0],
  });
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingEmployeeId, setEditingEmployeeId] = useState(null);

  // Work Areas State
  const [workAreas, setWorkAreas] = useState({});
  const [showWorkAreaModal, setShowWorkAreaModal] = useState(false);
  const [workAreaFormData, setWorkAreaFormData] = useState({
    employee_id: '', location_name: '', latitude: '', longitude: '', radius_meters: 100, address: ''
  });

  // Search / filter state
  const [searchQuery, setSearchQuery] = useState('');

  // Shared state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [processing, setProcessing] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  // Fetch all data on mount and when main tab changes
  useEffect(() => {
    loadTabData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainTab]);

  const loadTabData = async () => {
    setLoading(true);
    setError(null);
    try {
      switch (mainTab) {
        case 0:
          // Load Employees
          await loadEmployees();
          break;
        case 1:
          // Load Late Marks
          await loadLateMarks();
          break;
        case 2:
          // Load Leave Approvals
          await loadLeaveData();
          break;
        case 3:
          // Load Shift Management
          await loadShiftData();
          break;
        case 4:
          // Load Work Areas
          await loadWorkAreasTab();
          break;
        default:
          break;
      }
    } catch (err) {
      console.error('Error loading data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ==================== EMPLOYEE LIST ====================
  const loadEmployees = async () => {
    try {
      const [empResponse, shiftResponse] = await Promise.allSettled([
        employeeService.getEmployees(),
        attendanceService.getAllShifts()
      ]);
      setEmployees(empResponse.status === 'fulfilled' ? (empResponse.value.data?.data || []) : []);
      setShifts(shiftResponse.status === 'fulfilled' ? (shiftResponse.value.shifts || []) : []);
    } catch (err) {
      console.error('Error loading employees:', err);
      setError('Failed to load employees');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const formatTime = (timeString) => {
    if (!timeString) return 'N/A';
    return timeString.substring(0, 5);
  };

  // ==================== LATE MARK REPORTING ====================
  const loadLateMarks = async () => {
    try {
      const response = await attendanceService.getAllLateMarks();
      const marks = response.late_marks || [];
      setLateMarks(marks);
      
      // Calculate statistics
      const thisMonth = marks.filter(m => {
        const date = new Date(m.date);
        const now = new Date();
        return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear();
      });
      
      setLateMarkStats({
        total: marks.length,
        thisMonth: thisMonth.length,
        converted: marks.filter(m => m.is_converted_to_leave).length,
        pending: marks.filter(m => !m.is_converted_to_leave).length
      });
    } catch (err) {
      console.error('Error loading late marks:', err);
      setError('Failed to load late marks');
    }
  };

  // ==================== LEAVE APPROVAL ====================
  const loadLeaveData = async () => {
    try {
      // Fetch pending requests
      const pendingResponse = await attendanceService.getPendingLeaveRequests();
      setPendingRequests(pendingResponse.leave_requests || []);

      // Fetch all requests
      const allResponse = await attendanceService.getAllLeaveRequests();
      setLeaveRequests(allResponse.leave_requests || []);

      // Fetch leave balances
      const balancesResponse = await attendanceService.getAllLeaveBalances();
      setLeaveBalances(balancesResponse.leave_balances || []);

      // Calculate statistics
      const allRequests = allResponse.leave_requests || [];
      setLeaveStats({
        total: allRequests.length,
        pending: allRequests.filter(r => r.status === 'pending').length,
        approved: allRequests.filter(r => r.status === 'approved').length,
        rejected: allRequests.filter(r => r.status === 'rejected').length
      });
    } catch (err) {
      console.error('Error loading leave data:', err);
      setError('Failed to load leave data');
    }
  };

  const getLeaveTypeColor = (leaveType) => {
    switch (leaveType) {
      case 'CL': return 'primary';
      case 'SL': return 'secondary';
      case 'EL': return 'info';
      case 'LMD': return 'error';
      default: return 'default';
    }
  };

  const openDialog = (type, request) => {
    setDialogType(type);
    setSelectedRequest(request);
    setAdminNotes('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setSelectedRequest(null);
    setAdminNotes('');
  };

  const handleApproveLeave = async () => {
    setProcessing(true);
    try {
      await attendanceService.approveLeaveRequest(selectedRequest.id, { admin_notes: adminNotes });
      setSuccess('Leave request approved successfully');
      closeDialog();
      await loadLeaveData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to approve leave request');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectLeave = async () => {
    if (!adminNotes.trim()) {
      setError('Please provide a rejection reason');
      return;
    }
    setProcessing(true);
    try {
      await attendanceService.rejectLeaveRequest(selectedRequest.id, {
        rejection_reason: adminNotes,
        comments: adminNotes
      });
      setSuccess('Leave request rejected successfully');
      closeDialog();
      await loadLeaveData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to reject leave request');
    } finally {
      setProcessing(false);
    }
  };

  // ==================== SHIFT MANAGEMENT ====================
  const loadShiftData = async () => {
    try {
      const [employeesRes, shiftsRes] = await Promise.all([
        employeeService.getEmployees(),
        attendanceService.getAllShifts()
      ]);

      setEmployees_shifts(employeesRes.data?.data || []);
      setShifts(shiftsRes.shifts || []);

      const assigned = (employeesRes.data?.data || []).filter(e => e.shift_id).length;
      setShiftStats({
        totalEmployees: employeesRes.data?.data?.length || 0,
        assigned: assigned,
        unassigned: (employeesRes.data?.data?.length || 0) - assigned
      });
    } catch (err) {
      console.error('Error loading shift data:', err);
      setError('Failed to load shift data');
    }
  };

  const getShiftName = (shiftId) => {
    const shift = shifts.find(s => s.id === shiftId);
    return shift ? shift.name : 'N/A';
  };

  const generateEmployeeId = (department) => {
    if (!department) return '';
    const now = new Date();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const year = now.getFullYear();
    const dept = department.trim().toLowerCase();
    let prefix = 'sai';
    if (dept === 'mess') prefix = 'saimess';
    else if (dept === 'hostel') prefix = 'saihostel';
    else prefix = `sai${dept.replace(/\s+/g, '')}`;
    const dateStr = `${month}${year}`;
    const fullPrefix = `${prefix}${dateStr}`;
    const existingNums = employees
      .filter(emp => (emp.employee_id || '').toLowerCase().startsWith(fullPrefix))
      .map(emp => {
        const numStr = (emp.employee_id || '').slice(fullPrefix.length);
        return parseInt(numStr, 10) || 0;
      });
    const nextNum = existingNums.length > 0 ? Math.max(...existingNums) + 1 : 1;
    return `${fullPrefix}${String(nextNum).padStart(3, '0')}`;
  };

  const handleOpenAddEmployeeDialog = () => {
    setIsEditMode(false);
    setEditingEmployeeId(null);
    setAddEmployeeFormData({
      employee_id: '',
      first_name: '',
      last_name: '',
      email: '',
      username: '',
      password: '',
      phone: '',
      role: '',
      department: '',
      joining_date: new Date().toISOString().split('T')[0],
    });
    setSelectedPhoto(null);
    setAddEmployeeDialogOpen(true);
  };

  const handleOpenEditEmployeeDialog = (employee) => {
    setIsEditMode(true);
    setEditingEmployeeId(employee.id);
    setAddEmployeeFormData({
      employee_id: employee.employee_id || '',
      first_name: employee.user?.first_name || '',
      last_name: employee.user?.last_name || '',
      email: employee.user?.email || '',
      username: employee.user?.username || '',
      password: '',
      phone: employee.user?.phone || '',
      role: employee.designation || '',
      department: employee.department || '',
      joining_date: employee.joining_date || new Date().toISOString().split('T')[0],
    });
    if (employee.user?.profile_photo_url) {
      setSelectedPhoto(employee.user.profile_photo_url);
    } else {
      setSelectedPhoto(null);
    }
    setAddEmployeeDialogOpen(true);
  };

  const handleAddEmployeeFormChange = (e) => {
    const { name, value } = e.target;
    setAddEmployeeFormData((prev) => {
      const updated = { ...prev, [name]: value };
      // Auto-generate employee ID when department changes (only add mode)
      if (name === 'department' && !isEditMode) {
        updated.employee_id = generateEmployeeId(value);
      }
      return updated;
    });
  };

  const handlePhotoFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setSelectedPhoto(file);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmitAddEmployee = async () => {
    const { first_name, last_name, username } = addEmployeeFormData;
    if (!first_name || !last_name || !username) {
      setError('Please fill First Name, Last Name and Username');
      return;
    }

    setProcessing(true);
    try {
      const formData = new FormData();
      formData.append('employee_id', addEmployeeFormData.employee_id);
      formData.append('first_name', addEmployeeFormData.first_name);
      formData.append('last_name', addEmployeeFormData.last_name);
      formData.append('email', addEmployeeFormData.email);
      formData.append('username', addEmployeeFormData.username);
      if (addEmployeeFormData.password) {
        formData.append('password', addEmployeeFormData.password);
      }
      formData.append('phone', addEmployeeFormData.phone);
      formData.append('role', addEmployeeFormData.role);
      formData.append('designation', addEmployeeFormData.role);
      formData.append('department', addEmployeeFormData.department);
      formData.append('joining_date', addEmployeeFormData.joining_date);
      if (selectedPhoto && selectedPhoto instanceof File) {
        formData.append('profile_photo', selectedPhoto);
      }

      let response;
      if (isEditMode) {
        response = await apiClient.put(`/users/employees/${editingEmployeeId}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } else {
        response = await apiClient.post('/users/employees', formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      }

      if (response.data.success !== false) {
        setSuccess(isEditMode ? 'Employee updated successfully' : 'Employee added successfully');
        setAddEmployeeDialogOpen(false);
        await loadTabData();
      } else {
        setError(response.data.message || 'Failed to save employee');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Error saving employee: ' + err.message);
    } finally {
      setProcessing(false);
    }
  };

  const handleAssignShift = async () => {
    if (!shiftDialogEmployee || !shiftDialogShiftId || !shiftDialogDate) {
      setError('Please select a shift and effective date');
      return;
    }
    setProcessing(true);
    try {
      const res = await attendanceService.assignShift(
        shiftDialogEmployee.id,
        parseInt(shiftDialogShiftId),
        shiftDialogDate
      );
      if (res.success) {
        setSuccess(`Shift assigned to ${shiftDialogEmployee.user?.first_name} ${shiftDialogEmployee.user?.last_name} successfully!`);
        setShiftDialogOpen(false);
        setShiftDialogEmployee(null);
        setShiftDialogShiftId('');
        setShiftDialogDate(new Date().toISOString().split('T')[0]);
        await loadShiftData();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(res.message || 'Failed to assign shift');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign shift');
    } finally {
      setProcessing(false);
    }
  };

  // ==================== WORK AREAS ====================
  const loadWorkAreasTab = async () => {
    try {
      const response = await employeeService.getEmployees();
      const empList = response.data?.data || [];
      setEmployees(empList);
      const workAreaMap = {};
      for (const emp of empList) {
        try {
          const res = await apiClient.get(`/attendance/admin/work-area/${emp.id}`);
          workAreaMap[emp.id] = res.data.success ? res.data.work_area : null;
        } catch {
          workAreaMap[emp.id] = null;
        }
      }
      setWorkAreas(workAreaMap);
    } catch (err) {
      setError('Failed to load work areas');
    }
  };

  const handleWorkAreaInputChange = (e) => {
    const { name, value } = e.target;
    setWorkAreaFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleUseCurrentLocation = () => {
    if (!navigator.geolocation) { alert('Geolocation is not supported'); return; }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords;
        setWorkAreaFormData(prev => ({ ...prev, latitude: latitude.toFixed(6), longitude: longitude.toFixed(6) }));
        setSuccess(`Location captured: (${latitude.toFixed(6)}, ${longitude.toFixed(6)})`);
        setTimeout(() => setSuccess(null), 3000);
      },
      (error) => {
        let msg = 'Failed to get location';
        if (error.code === 1) msg = 'Location permission denied.';
        else if (error.code === 2) msg = 'Location unavailable.';
        else if (error.code === 3) msg = 'Location request timed out.';
        setError(msg);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const handleSaveWorkArea = async (e) => {
    e.preventDefault();
    if (!workAreaFormData.employee_id || !workAreaFormData.location_name ||
        !workAreaFormData.latitude || !workAreaFormData.longitude) {
      setError('Please fill all required fields');
      return;
    }
    setProcessing(true);
    try {
      const payload = {
        employee_id: parseInt(workAreaFormData.employee_id),
        location_name: workAreaFormData.location_name,
        latitude: parseFloat(workAreaFormData.latitude),
        longitude: parseFloat(workAreaFormData.longitude),
        radius_meters: parseFloat(workAreaFormData.radius_meters) || 100,
        address: workAreaFormData.address
      };
      const response = await apiClient.post('/attendance/admin/work-area/set', payload);
      if (response.data.success) {
        setSuccess('Work area assigned successfully!');
        setWorkAreaFormData({ employee_id: '', location_name: '', latitude: '', longitude: '', radius_meters: 100, address: '' });
        setShowWorkAreaModal(false);
        await loadWorkAreasTab();
        setTimeout(() => setSuccess(null), 3000);
      } else {
        setError(response.data.message || 'Failed to assign work area');
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save work area');
    } finally {
      setProcessing(false);
    }
  };

  // ==================== RENDER ====================
  const TAB_CONFIG = [
    { label: 'Employee List',      icon: <PeopleIcon />,    color: '#2196f3' },
    { label: 'Late Mark Reporting',icon: <LateMarkIcon />,  color: '#ff9800' },
    { label: 'Leave Approval',     icon: <LeaveIcon />,     color: '#9c27b0' },
    { label: 'Shift Management',   icon: <ShiftIcon />,     color: '#00bcd4' },
    { label: 'Work Areas',         icon: <WorkAreaIcon />,  color: '#4caf50' },
  ];

  const activeColor = TAB_CONFIG[mainTab]?.color || '#2196f3';

  if (loading) {
    return (
      <Layout>
        <Container maxWidth="lg" sx={{ mt: 6, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ color: activeColor }} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>Loading data…</Typography>
        </Container>
      </Layout>
    );
  }

  const filteredEmployees = employees.filter(emp => {
    if (!searchQuery.trim()) return true;
    const q = searchQuery.toLowerCase();
    return (
      (emp.user?.first_name + ' ' + emp.user?.last_name).toLowerCase().includes(q) ||
      (emp.user?.email || '').toLowerCase().includes(q) ||
      (emp.employee_id || '').toString().toLowerCase().includes(q) ||
      (emp.department || '').toLowerCase().includes(q)
    );
  });

  return (
    <Layout title="Employees">
      <Container maxWidth="lg" sx={{ mt: 3, mb: 5 }}>

        {/* ── Page Header ── */}
        <Box sx={{
          background: 'linear-gradient(135deg, #1565c0 0%, #2196f3 60%, #42a5f5 100%)',
          borderRadius: 3,
          p: { xs: 2.5, md: 3.5 },
          mb: 3,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 4px 20px rgba(33,150,243,0.35)'
        }}>
          <Box>
            <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: 0.3 }}>
              👔 Employee Management
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.85, mt: 0.5 }}>
              Manage employees, late marks, leave approvals, shifts &amp; work areas
            </Typography>
          </Box>
          <Tooltip title="Refresh current tab">
            <IconButton onClick={loadTabData} sx={{ color: '#fff', backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {/* ── Alerts ── */}
        {error   && <Alert severity="error"   sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError(null)}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess(null)}>{success}</Alert>}

        {/* ── Main Card with Tabs ── */}
        <Paper elevation={2} sx={{ borderRadius: 3, overflow: 'hidden' }}>

          {/* Tabs */}
          <Box sx={{ backgroundColor: '#fafafa', borderBottom: '1px solid #e0e0e0' }}>
            <Tabs
              value={mainTab}
              onChange={(e, v) => setMainTab(v)}
              variant="scrollable"
              scrollButtons="auto"
              TabIndicatorProps={{ style: { height: 3, borderRadius: 3, backgroundColor: activeColor } }}
              sx={{
                '& .MuiTab-root': { minHeight: 56, textTransform: 'none', fontWeight: 500, fontSize: '0.85rem', color: '#555', gap: 0.5 },
                '& .Mui-selected': { color: `${activeColor} !important`, fontWeight: 700 },
              }}
            >
              {TAB_CONFIG.map((tab, i) => (
                <Tab key={i} icon={tab.icon} iconPosition="start" label={tab.label}
                  id={`employee-tab-${i}`} aria-controls={`employee-tabpanel-${i}`} />
              ))}
            </Tabs>
          </Box>


          {/* ══ TAB 0: EMPLOYEE LIST ══ */}
          <TabPanel value={mainTab} index={0}>
            {/* Search bar and Add Button */}
            <Box sx={{ mb: 2.5, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Search by name, email, employee ID or department…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ color: '#9e9e9e' }} /></InputAdornment>,
                  sx: { borderRadius: 2, backgroundColor: '#fafafa' }
                }}
              />
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleOpenAddEmployeeDialog}
                sx={{
                  backgroundColor: '#84cc16',
                  color: '#fff',
                  fontWeight: 600,
                  borderRadius: 2,
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: '#65a30d' }
                }}
              >
                Add Employee
              </Button>
            </Box>
            <Box sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Showing <strong>{filteredEmployees.length}</strong> of <strong>{employees.length}</strong> employees
              </Typography>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#1565c020' }}>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }}>Employee</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }}>Department</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }}>Shift</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }} align="center">Status</TableCell>
                    <TableCell sx={{ fontWeight: 700, color: '#1565c0', py: 1.5 }} align="center">Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredEmployees.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <PeopleIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                        <Typography color="text.secondary">No employees found</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredEmployees.map((emp, idx) => {
                      const initials = `${emp.user?.first_name?.charAt(0) || ''}${emp.user?.last_name?.charAt(0) || ''}`.toUpperCase();
                      const avatarColor = ['#2196f3','#9c27b0','#ff9800','#4caf50','#f44336','#00bcd4'][emp.id % 6];
                      return (
                        <TableRow key={emp.id} hover sx={{ '&:hover': { backgroundColor: '#f0f7ff' } }}>
                          <TableCell sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>#{emp.employee_id || emp.id}</TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar sx={{ width: 36, height: 36, fontSize: '0.85rem', fontWeight: 700, backgroundColor: avatarColor }}>
                                {emp.user?.profile_photo_url ? (
                                  <img src={emp.user.profile_photo_url.startsWith('http') ? emp.user.profile_photo_url : `${serverUrl}${emp.user.profile_photo_url}`}
                                    alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                                ) : initials}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.2 }}>
                                  {emp.user?.first_name} {emp.user?.last_name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">{emp.designation || '—'}</Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{emp.user?.email || '—'}</Typography>
                            <Typography variant="caption" color="text.secondary">{emp.user?.phone || ''}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{emp.department || <span style={{ color: '#bdbdbd' }}>—</span>}</Typography>
                          </TableCell>
                          <TableCell>
                            {emp.shift_id
                              ? <Chip label={getShiftName(emp.shift_id)} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem' }} />
                              : <Chip label="No Shift" size="small" variant="outlined" sx={{ fontSize: '0.7rem', color: '#9e9e9e', borderColor: '#e0e0e0' }} />}
                          </TableCell>
                          <TableCell align="center">
                            <Chip
                              label={emp.status === 'active' ? 'Active' : (emp.status || 'Active')}
                              size="small"
                              sx={{
                                backgroundColor: emp.status === 'inactive' ? '#fce4e4' : '#e8f5e9',
                                color: emp.status === 'inactive' ? '#c62828' : '#2e7d32',
                                fontWeight: 600, fontSize: '0.7rem'
                              }}
                            />
                          </TableCell>
                          <TableCell align="center">
                            <Tooltip title="View Details">
                              <IconButton size="small" color="primary" onClick={() => { setViewEmployee(emp); setViewDialogOpen(true); }}>
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Edit Employee">
                              <IconButton size="small" color="success" onClick={() => handleOpenEditEmployeeDialog(emp)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>


          {/* ══ TAB 1: LATE MARK REPORTING ══ */}
          <TabPanel value={mainTab} index={1}>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Late Marks',    value: lateMarkStats.total,     icon: <LateMarkIcon />,   bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
                { label: 'This Month',          value: lateMarkStats.thisMonth, icon: <TodayIcon />,      bg: '#fff3e0', color: '#e65100', border: '#ffcc80' },
                { label: 'Converted to Leave',  value: lateMarkStats.converted, icon: <ConvertedIcon />,  bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
                { label: 'Pending Conversion',  value: lateMarkStats.pending,   icon: <PendingIcon />,    bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
              ].map((s, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, backgroundColor: s.bg, border: `1px solid ${s.border}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: s.color, display: 'flex' }}>{s.icon}</Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                        <Typography variant="caption" sx={{ color: s.color, opacity: 0.8 }}>{s.label}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'rgba(255,152,0,0.12)' }}>
                    {['#','Employee','Date','Expected','Actual','Late (mins)','Status'].map(h => (
                      <TableCell key={h} align={['Late (mins)','Status'].includes(h) ? 'center' : 'left'}
                        sx={{ fontWeight: 700, color: '#e65100', py: 1.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {lateMarks.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <LateMarkIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                      <Typography color="text.secondary">No late marks found</Typography>
                    </TableCell></TableRow>
                  ) : lateMarks.map((mark) => (
                    <TableRow key={mark.id} hover sx={{ '&:hover': { backgroundColor: '#fffde7' } }}>
                      <TableCell sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>#{mark.id}</TableCell>
                      <TableCell><Typography variant="body2" fontWeight={500}>{mark.employee_name}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatDate(mark.date)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatTime(mark.expected_time)}</Typography></TableCell>
                      <TableCell><Typography variant="body2">{formatTime(mark.actual_time)}</Typography></TableCell>
                      <TableCell align="center">
                        <Chip label={`${mark.delay_minutes} min`}
                          size="small"
                          sx={{
                            fontWeight: 700, fontSize: '0.72rem',
                            backgroundColor: mark.delay_minutes >= 60 ? '#fce4e4' : mark.delay_minutes >= 30 ? '#fff3e0' : '#e3f2fd',
                            color: mark.delay_minutes >= 60 ? '#c62828' : mark.delay_minutes >= 30 ? '#e65100' : '#1565c0',
                          }} />
                      </TableCell>
                      <TableCell align="center">
                        <Chip label={mark.is_converted_to_leave ? 'Converted' : 'Pending'}
                          size="small"
                          sx={{ fontWeight: 600, fontSize: '0.72rem',
                            backgroundColor: mark.is_converted_to_leave ? '#e8f5e9' : '#fff8e1',
                            color: mark.is_converted_to_leave ? '#2e7d32' : '#f57f17' }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>


          {/* ══ TAB 2: LEAVE APPROVAL ══ */}
          <TabPanel value={mainTab} index={2}>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Requests',  value: leaveStats.total,    icon: <LeaveIcon />,     bg: '#ede7f6', color: '#4527a0', border: '#b39ddb' },
                { label: 'Pending',         value: leaveStats.pending,  icon: <PendingIcon />,   bg: '#fff8e1', color: '#f57f17', border: '#ffe082' },
                { label: 'Approved',        value: leaveStats.approved, icon: <ApprovedIcon />,  bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
                { label: 'Rejected',        value: leaveStats.rejected, icon: <RejectedIcon />,  bg: '#fce4e4', color: '#c62828', border: '#ef9a9a' },
              ].map((s, i) => (
                <Grid item xs={6} md={3} key={i}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, backgroundColor: s.bg, border: `1px solid ${s.border}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: s.color }}>{s.icon}</Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                        <Typography variant="caption" sx={{ color: s.color, opacity: 0.8 }}>{s.label}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>

            {/* Sub-tabs */}
            <Box sx={{ borderBottom: '2px solid #ede7f6', mb: 2 }}>
              <Tabs value={leaveSubTab} onChange={(e, v) => setLeaveSubTab(v)}
                TabIndicatorProps={{ style: { backgroundColor: '#9c27b0', height: 2 } }}
                sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 500 }, '& .Mui-selected': { color: '#9c27b0 !important', fontWeight: 700 } }}>
                <Tab label="⏳ Pending Approval" />
                <Tab label="📋 All Requests" />
                <Tab label="💳 Leave Balances" />
              </Tabs>
            </Box>

            {/* Pending */}
            {leaveSubTab === 0 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#9c27b020' }}>
                      {['#','Employee','Leave Type','From','To','Days','Actions'].map(h => (
                        <TableCell key={h} align={['Days','Actions'].includes(h) ? 'center' : 'left'}
                          sx={{ fontWeight: 700, color: '#6a1b9a', py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pendingRequests.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <PendingIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                        <Typography color="text.secondary">No pending leave requests</Typography>
                      </TableCell></TableRow>
                    ) : pendingRequests.map((req) => (
                      <TableRow key={req.id} hover sx={{ '&:hover': { backgroundColor: '#f3e5f5' } }}>
                        <TableCell sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>#{req.id}</TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{req.employee_name}</Typography></TableCell>
                        <TableCell><Chip label={`${req.leave_type_name} (${req.leave_type_code})`} size="small" color={getLeaveTypeColor(req.leave_type_code)} sx={{ fontSize: '0.7rem' }} /></TableCell>
                        <TableCell><Typography variant="body2">{formatDate(req.from_date)}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{formatDate(req.to_date)}</Typography></TableCell>
                        <TableCell align="center"><Chip label={req.number_of_days} size="small" sx={{ backgroundColor: '#ede7f6', color: '#4527a0', fontWeight: 700 }} /></TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title="Approve"><IconButton size="small" sx={{ color: '#2e7d32', backgroundColor: '#e8f5e9', '&:hover': { backgroundColor: '#c8e6c9' } }} onClick={() => openDialog('approve', req)}><ApproveIcon fontSize="small" /></IconButton></Tooltip>
                            <Tooltip title="Reject"><IconButton size="small" sx={{ color: '#c62828', backgroundColor: '#fce4e4', '&:hover': { backgroundColor: '#ffcdd2' } }} onClick={() => openDialog('reject', req)}><RejectIcon fontSize="small" /></IconButton></Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* All Requests */}
            {leaveSubTab === 1 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#9c27b020' }}>
                      {['#','Employee','Leave Type','From','To','Days','Status'].map(h => (
                        <TableCell key={h} align={['Days','Status'].includes(h) ? 'center' : 'left'}
                          sx={{ fontWeight: 700, color: '#6a1b9a', py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaveRequests.length === 0 ? (
                      <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                        <LeaveIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                        <Typography color="text.secondary">No leave requests found</Typography>
                      </TableCell></TableRow>
                    ) : leaveRequests.map((req) => (
                      <TableRow key={req.id} hover sx={{ '&:hover': { backgroundColor: '#f3e5f5' } }}>
                        <TableCell sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>#{req.id}</TableCell>
                        <TableCell><Typography variant="body2" fontWeight={500}>{req.employee_name}</Typography></TableCell>
                        <TableCell><Chip label={`${req.leave_type_name} (${req.leave_type_code})`} size="small" color={getLeaveTypeColor(req.leave_type_code)} sx={{ fontSize: '0.7rem' }} /></TableCell>
                        <TableCell><Typography variant="body2">{formatDate(req.from_date)}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{formatDate(req.to_date)}</Typography></TableCell>
                        <TableCell align="center"><Chip label={req.number_of_days} size="small" sx={{ backgroundColor: '#ede7f6', color: '#4527a0', fontWeight: 700 }} /></TableCell>
                        <TableCell align="center">
                          <Chip label={req.status.toUpperCase()} size="small"
                            sx={{ fontWeight: 600, fontSize: '0.7rem',
                              backgroundColor: req.status === 'approved' ? '#e8f5e9' : req.status === 'rejected' ? '#fce4e4' : '#fff8e1',
                              color: req.status === 'approved' ? '#2e7d32' : req.status === 'rejected' ? '#c62828' : '#f57f17' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Balances */}
            {leaveSubTab === 2 && (
              <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: '#9c27b020' }}>
                      {['Employee','Leave Type','Total','Used','Pending','Available'].map(h => (
                        <TableCell key={h} align={['Total','Used','Pending','Available'].includes(h) ? 'center' : 'left'}
                          sx={{ fontWeight: 700, color: '#6a1b9a', py: 1.5 }}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {leaveBalances.length === 0 ? (
                      <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography color="text.secondary">No leave balance data</Typography>
                      </TableCell></TableRow>
                    ) : leaveBalances.map((bal) => (
                      <TableRow key={`${bal.employee_id}-${bal.leave_type_id}`} hover sx={{ '&:hover': { backgroundColor: '#f3e5f5' } }}>
                        <TableCell><Typography variant="body2" fontWeight={500}>{bal.employee_name}</Typography></TableCell>
                        <TableCell><Typography variant="body2">{bal.leave_type?.name}</Typography></TableCell>
                        <TableCell align="center">{bal.total_allocated}</TableCell>
                        <TableCell align="center">{bal.used_days}</TableCell>
                        <TableCell align="center">{bal.pending_days}</TableCell>
                        <TableCell align="center">
                          <Chip label={bal.available_days.toFixed(1)} size="small"
                            sx={{ fontWeight: 700,
                              backgroundColor: bal.available_days > 0 ? '#e8f5e9' : '#fce4e4',
                              color: bal.available_days > 0 ? '#2e7d32' : '#c62828' }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </TabPanel>


          {/* ══ TAB 3: SHIFT MANAGEMENT ══ */}
          <TabPanel value={mainTab} index={3}>
            {/* Stats */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              {[
                { label: 'Total Employees', value: shiftStats.totalEmployees, icon: <PeopleIcon />,         bg: '#e3f2fd', color: '#1565c0', border: '#90caf9' },
                { label: 'Shift Assigned',  value: shiftStats.assigned,       icon: <ShiftAssignedIcon />,  bg: '#e8f5e9', color: '#2e7d32', border: '#a5d6a7' },
                { label: 'Unassigned',      value: shiftStats.unassigned,     icon: <UnassignedIcon />,     bg: '#fce4e4', color: '#c62828', border: '#ef9a9a' },
              ].map((s, i) => (
                <Grid item xs={12} md={4} key={i}>
                  <Paper elevation={0} sx={{ p: 2.5, borderRadius: 2, backgroundColor: s.bg, border: `1px solid ${s.border}` }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box sx={{ color: s.color }}>{s.icon}</Box>
                      <Box>
                        <Typography variant="h5" fontWeight={700} sx={{ color: s.color, lineHeight: 1 }}>{s.value}</Typography>
                        <Typography variant="caption" sx={{ color: s.color, opacity: 0.8 }}>{s.label}</Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>
              ))}
            </Grid>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#00bcd420' }}>
                    {['#','Employee','Email','Assigned Shift','Action'].map(h => (
                      <TableCell key={h} align={h === 'Action' ? 'center' : 'left'}
                        sx={{ fontWeight: 700, color: '#00695c', py: 1.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees_shifts.length === 0 ? (
                    <TableRow><TableCell colSpan={5} align="center" sx={{ py: 6 }}>
                      <ShiftIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                      <Typography color="text.secondary">No employees found</Typography>
                    </TableCell></TableRow>
                  ) : employees_shifts.map((emp) => (
                    <TableRow key={emp.id} hover sx={{ '&:hover': { backgroundColor: '#e0f7fa' } }}>
                      <TableCell sx={{ color: '#9e9e9e', fontSize: '0.75rem' }}>#{emp.employee_id || emp.id}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{emp.user?.first_name} {emp.user?.last_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{emp.department || ''}</Typography>
                      </TableCell>
                      <TableCell><Typography variant="body2">{emp.user?.email}</Typography></TableCell>
                      <TableCell>
                        {emp.shift_id
                          ? <Chip label={getShiftName(emp.shift_id)} size="small" sx={{ backgroundColor: '#e0f7fa', color: '#00695c', fontWeight: 600, fontSize: '0.7rem' }} />
                          : <Chip label="Not Assigned" size="small" sx={{ backgroundColor: '#fce4e4', color: '#c62828', fontWeight: 600, fontSize: '0.7rem' }} />}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip title={emp.shift_id ? 'Change Shift' : 'Assign Shift'}>
                          <IconButton size="small"
                            sx={{ color: '#00695c', backgroundColor: '#e0f7fa', '&:hover': { backgroundColor: '#b2ebf2' } }}
                            onClick={() => {
                              setShiftDialogEmployee(emp);
                              setShiftDialogShiftId(emp.shift_id ? String(emp.shift_id) : '');
                              setShiftDialogDate(new Date().toISOString().split('T')[0]);
                              setShiftDialogOpen(true);
                            }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          {/* ══ TAB 4: WORK AREAS ══ */}
          <TabPanel value={mainTab} index={4}>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
              <Button variant="contained"
                sx={{ background: 'linear-gradient(90deg,#388e3c,#66bb6a)', borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
                startIcon={<WorkAreaIcon />}
                onClick={() => { setWorkAreaFormData({ employee_id: '', location_name: '', latitude: '', longitude: '', radius_meters: 100, address: '' }); setShowWorkAreaModal(true); }}>
                Assign Work Area
              </Button>
            </Box>
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, overflow: 'hidden' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: '#4caf5020' }}>
                    {['Employee','Location','Coordinates','Radius','Address','Status','Action'].map(h => (
                      <TableCell key={h} align={['Radius','Status','Action'].includes(h) ? 'center' : 'left'}
                        sx={{ fontWeight: 700, color: '#2e7d32', py: 1.5 }}>{h}</TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {employees.length === 0 ? (
                    <TableRow><TableCell colSpan={7} align="center" sx={{ py: 6 }}>
                      <WorkAreaIcon sx={{ fontSize: 48, color: '#e0e0e0', mb: 1 }} />
                      <Typography color="text.secondary">No employees found</Typography>
                    </TableCell></TableRow>
                  ) : employees.map((emp) => {
                    const wa = workAreas[emp.id];
                    return (
                      <TableRow key={emp.id} hover sx={{ '&:hover': { backgroundColor: '#f1f8e9' } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600}>{emp.user?.first_name} {emp.user?.last_name}</Typography>
                          <Typography variant="caption" color="text.secondary">{emp.employee_id}</Typography>
                        </TableCell>
                        <TableCell>
                          {wa ? <Typography variant="body2" fontWeight={500}>{wa.location_name}</Typography>
                              : <Chip label="Not Assigned" size="small" sx={{ backgroundColor: '#fce4e4', color: '#c62828', fontSize: '0.7rem' }} />}
                        </TableCell>
                        <TableCell>
                          {wa ? <Typography variant="caption" sx={{ fontFamily: 'monospace', backgroundColor: '#f5f5f5', p: 0.5, borderRadius: 1 }}>
                            {parseFloat(wa.latitude).toFixed(4)}, {parseFloat(wa.longitude).toFixed(4)}
                          </Typography> : <span style={{ color: '#bdbdbd' }}>—</span>}
                        </TableCell>
                        <TableCell align="center">
                          {wa ? <Chip label={`${wa.radius_meters}m`} size="small" sx={{ backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 600, fontSize: '0.7rem' }} /> : '—'}
                        </TableCell>
                        <TableCell><Typography variant="body2" sx={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{wa?.address || '—'}</Typography></TableCell>
                        <TableCell align="center">
                          <Chip label={wa ? 'Assigned' : 'Pending'} size="small"
                            sx={{ fontWeight: 600, fontSize: '0.7rem',
                              backgroundColor: wa ? '#e8f5e9' : '#fff8e1',
                              color: wa ? '#2e7d32' : '#f57f17' }} />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={wa ? 'Edit Work Area' : 'Assign Work Area'}>
                            <IconButton size="small"
                              sx={{ color: wa ? '#388e3c' : '#f57f17', backgroundColor: wa ? '#e8f5e9' : '#fff8e1', '&:hover': { backgroundColor: wa ? '#c8e6c9' : '#ffe0b2' } }}
                              onClick={() => { setWorkAreaFormData({ employee_id: emp.id.toString(), location_name: wa?.location_name || '', latitude: wa?.latitude?.toString() || '', longitude: wa?.longitude?.toString() || '', radius_meters: wa?.radius_meters || 100, address: wa?.address || '' }); setShowWorkAreaModal(true); }}>
                              {wa ? <EditIcon fontSize="small" /> : <WorkAreaIcon fontSize="small" />}
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>
        </Paper>
      </Container>


      {/* ── Leave Approval Dialog ── */}
      <Dialog open={dialogOpen} onClose={closeDialog} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{
          background: dialogType === 'approve' ? 'linear-gradient(135deg,#2e7d32,#66bb6a)' : 'linear-gradient(135deg,#c62828,#ef5350)',
          color: '#fff', display: 'flex', alignItems: 'center', gap: 1
        }}>
          {dialogType === 'approve' ? <ApproveIcon /> : <RejectIcon />}
          {dialogType === 'approve' ? 'Approve Leave Request' : 'Reject Leave Request'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedRequest && (
            <>
              <Paper elevation={0} sx={{ p: 2, mb: 2.5, backgroundColor: '#f8f9fa', borderRadius: 2, border: '1px solid #e0e0e0' }}>
                <Grid container spacing={1}>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Employee</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedRequest.employee_name}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">Leave Type</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedRequest.leave_type_name}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">From</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(selectedRequest.from_date)}</Typography></Grid>
                  <Grid item xs={6}><Typography variant="caption" color="text.secondary">To</Typography>
                    <Typography variant="body2" fontWeight={600}>{formatDate(selectedRequest.to_date)}</Typography></Grid>
                  <Grid item xs={12}><Typography variant="caption" color="text.secondary">Total Days</Typography>
                    <Typography variant="body2" fontWeight={600}>{selectedRequest.number_of_days} day(s)</Typography></Grid>
                </Grid>
              </Paper>
              <TextField fullWidth multiline rows={4}
                label={dialogType === 'approve' ? 'Approval Notes (optional)' : 'Rejection Reason (required)'}
                placeholder={dialogType === 'approve' ? 'Add any notes for this approval…' : 'Why is this leave being rejected?'}
                value={adminNotes} onChange={(e) => setAdminNotes(e.target.value)}
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={closeDialog} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={dialogType === 'approve' ? handleApproveLeave : handleRejectLeave}
            variant="contained" disabled={processing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600,
              backgroundColor: dialogType === 'approve' ? '#2e7d32' : '#c62828',
              '&:hover': { backgroundColor: dialogType === 'approve' ? '#1b5e20' : '#b71c1c' } }}>
            {processing ? <CircularProgress size={22} color="inherit" /> : (dialogType === 'approve' ? '✓ Approve' : '✗ Reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── View Employee Dialog ── */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg,#1565c0,#42a5f5)', p: 3, color: '#fff', position: 'relative' }}>
          <IconButton onClick={() => setViewDialogOpen(false)} size="small"
            sx={{ position: 'absolute', top: 12, right: 12, color: 'rgba(255,255,255,0.8)', backgroundColor: 'rgba(255,255,255,0.15)', '&:hover': { backgroundColor: 'rgba(255,255,255,0.25)' } }}>✕</IconButton>
          {viewEmployee && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Avatar sx={{ width: 72, height: 72, fontSize: '1.6rem', fontWeight: 700, border: '3px solid rgba(255,255,255,0.5)',
                backgroundColor: ['#2196f3','#9c27b0','#ff9800','#4caf50','#f44336','#00bcd4'][(viewEmployee.id || 0) % 6] }}>
                {viewEmployee.user?.profile_photo_url ? (
                  <img src={viewEmployee.user.profile_photo_url.startsWith('http') ? viewEmployee.user.profile_photo_url : `${serverUrl}${viewEmployee.user.profile_photo_url}`}
                    alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; }} />
                ) : `${viewEmployee.user?.first_name?.charAt(0) || ''}${viewEmployee.user?.last_name?.charAt(0) || ''}`}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight={700}>{viewEmployee.user?.first_name} {viewEmployee.user?.last_name}</Typography>
                <Typography variant="body2" sx={{ opacity: 0.85 }}>{viewEmployee.designation || 'No designation'}</Typography>
                <Chip label={viewEmployee.status === 'inactive' ? 'Inactive' : 'Active'} size="small"
                  sx={{ mt: 0.5, backgroundColor: viewEmployee.status === 'inactive' ? '#ffcdd2' : '#c8e6c9',
                    color: viewEmployee.status === 'inactive' ? '#b71c1c' : '#1b5e20', fontWeight: 700, fontSize: '0.7rem' }} />
              </Box>
            </Box>
          )}
        </Box>
        <DialogContent sx={{ p: 0 }}>
          {viewEmployee && (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={0}>
                {[
                  { label: 'Employee ID',   value: viewEmployee.employee_id || 'N/A' },
                  { label: 'Username',      value: viewEmployee.user?.username || 'N/A' },
                  { label: 'Email',         value: viewEmployee.user?.email || 'N/A' },
                  { label: 'Phone',         value: viewEmployee.user?.phone || 'N/A' },
                  { label: 'Department',    value: viewEmployee.department || 'N/A' },
                  { label: 'Designation',   value: viewEmployee.designation || 'N/A' },
                  { label: 'Joining Date',  value: formatDate(viewEmployee.joining_date) },
                  { label: 'Shift',         value: viewEmployee.shift_id ? getShiftName(viewEmployee.shift_id) : 'Not Assigned' },
                ].map((item, i) => (
                  <Grid item xs={6} key={i}>
                    <Box sx={{ p: 1.5, borderBottom: '1px solid #f0f0f0', borderRight: i % 2 === 0 ? '1px solid #f0f0f0' : 'none' }}>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.3 }}>{item.label}</Typography>
                      <Typography variant="body2" fontWeight={600}>{item.value}</Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setViewDialogOpen(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ── Assign Shift Dialog ── */}
      <Dialog open={shiftDialogOpen} onClose={() => !processing && setShiftDialogOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg,#00695c,#26a69a)', p: 2.5, color: '#fff', display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ShiftIcon />
          <Box>
            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.2 }}>
              {shiftDialogEmployee?.shift_id ? 'Change Shift' : 'Assign Shift'}
            </Typography>
            {shiftDialogEmployee && (
              <Typography variant="body2" sx={{ opacity: 0.85 }}>
                {shiftDialogEmployee.user?.first_name} {shiftDialogEmployee.user?.last_name}
              </Typography>
            )}
          </Box>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {shiftDialogEmployee?.shift_id && (
              <Paper elevation={0} sx={{ backgroundColor: '#e0f7fa', border: '1px solid #80cbc4', borderRadius: 2, p: 1.5 }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>Current Shift:</strong> {getShiftName(shiftDialogEmployee.shift_id)}
                </Typography>
              </Paper>
            )}
            <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <InputLabel>Select Shift *</InputLabel>
              <Select
                value={shiftDialogShiftId}
                label="Select Shift *"
                onChange={(e) => setShiftDialogShiftId(e.target.value)}
              >
                <MenuItem value=""><em style={{ color: '#9e9e9e' }}>-- Select Shift --</em></MenuItem>
                {shifts.map((s) => (
                  <MenuItem key={s.id} value={String(s.id)}>
                    <Box>
                      <Typography variant="body2" fontWeight={600}>{s.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{s.start_time} – {s.end_time}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth label="Effective From *" type="date"
              value={shiftDialogDate}
              onChange={(e) => setShiftDialogDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setShiftDialogOpen(false)} disabled={processing} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleAssignShift} variant="contained" disabled={processing || !shiftDialogShiftId || !shiftDialogDate}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#00695c', '&:hover': { backgroundColor: '#004d40' } }}>
            {processing ? <CircularProgress size={22} color="inherit" /> : (shiftDialogEmployee?.shift_id ? 'Update Shift' : 'Assign Shift')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Work Area Modal ── */}
      <Dialog open={showWorkAreaModal} onClose={() => setShowWorkAreaModal(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <DialogTitle sx={{ background: 'linear-gradient(135deg,#2e7d32,#66bb6a)', color: '#fff', display: 'flex', alignItems: 'center', gap: 1 }}>
          <WorkAreaIcon /> Assign Work Area
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField select fullWidth label="Select Employee *" name="employee_id" value={workAreaFormData.employee_id} onChange={handleWorkAreaInputChange}
              SelectProps={{ native: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
              <option value="">-- Select Employee --</option>
              {employees.map((emp) => (
                <option key={emp.id} value={emp.id}>{emp.user?.first_name} {emp.user?.last_name} ({emp.employee_id})</option>
              ))}
            </TextField>
            <TextField fullWidth label="Location Name *" name="location_name" placeholder="e.g., Main Office"
              value={workAreaFormData.location_name} onChange={handleWorkAreaInputChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField fullWidth label="Latitude *" name="latitude" type="number" placeholder="e.g., 19.9641"
                  inputProps={{ step: '0.0001' }} value={workAreaFormData.latitude} onChange={handleWorkAreaInputChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={6}>
                <TextField fullWidth label="Longitude *" name="longitude" type="number" placeholder="e.g., 73.7836"
                  inputProps={{ step: '0.0001' }} value={workAreaFormData.longitude} onChange={handleWorkAreaInputChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
            </Grid>
            <Button variant="outlined" startIcon={<WorkAreaIcon />} onClick={handleUseCurrentLocation} fullWidth
              sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#0ea5e9', color: '#0ea5e9', fontWeight: 600,
                '&:hover': { borderColor: '#0284c7', backgroundColor: '#f0f9ff' } }}>
              📍 Use My Current Location
            </Button>
            <TextField fullWidth label="Radius (meters)" name="radius_meters" type="number"
              value={workAreaFormData.radius_meters} onChange={handleWorkAreaInputChange}
              inputProps={{ min: 10, step: 10 }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <TextField fullWidth label="Address" name="address" placeholder="Full address for reference"
              value={workAreaFormData.address} onChange={handleWorkAreaInputChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
            <Paper elevation={0} sx={{ backgroundColor: '#f0f9ff', border: '1px solid #bfdbfe', borderRadius: 2, p: 1.5 }}>
              <Typography variant="caption" color="text.secondary">
                <strong>Tip:</strong> Open Google Maps → search your location → right-click → copy coordinates
              </Typography>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={() => setShowWorkAreaModal(false)} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSaveWorkArea} variant="contained" disabled={processing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#2e7d32', '&:hover': { backgroundColor: '#1b5e20' } }}>
            {processing ? <CircularProgress size={22} color="inherit" /> : '📍 Save Work Area'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Add/Edit Employee Dialog ── */}
      <Dialog open={addEmployeeDialogOpen} onClose={() => !processing && setAddEmployeeDialogOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}>
        <Box sx={{ background: 'linear-gradient(135deg,#84cc16,#65a30d)', p: 2.5, color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <AddIcon fontSize="large" />
            <Box>
              <Typography variant="h6" fontWeight={700}>
                {isEditMode ? 'Edit Employee' : 'Add Employee'}
              </Typography>
            </Box>
          </Box>
          <IconButton onClick={() => setAddEmployeeDialogOpen(false)} disabled={processing} sx={{ color: 'rgba(255,255,255,0.8)' }}>✕</IconButton>
        </Box>
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Grid container spacing={2}>
              {/* Department FIRST — drives auto ID generation */}
              <Grid item xs={12}>
                <FormControl fullWidth sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}>
                  <InputLabel>Department *</InputLabel>
                  <Select
                    name="department"
                    label="Department *"
                    value={addEmployeeFormData.department}
                    onChange={handleAddEmployeeFormChange}
                  >
                    <MenuItem value=""><em style={{ color: '#9e9e9e' }}>-- Select Department --</em></MenuItem>
                    <MenuItem value="Mess">Mess</MenuItem>
                    <MenuItem value="Hostel">Hostel</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {/* Auto-generated Employee ID */}
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Employee ID (Auto-generated)"
                  name="employee_id"
                  value={addEmployeeFormData.employee_id}
                  onChange={handleAddEmployeeFormChange}
                  placeholder="Select department first"
                  InputProps={{ readOnly: !isEditMode && !!addEmployeeFormData.department }}
                  sx={{
                    '& .MuiOutlinedInput-root': { borderRadius: 2 },
                    '& input': { color: !isEditMode && addEmployeeFormData.department ? '#2e7d32' : undefined, fontWeight: !isEditMode && addEmployeeFormData.department ? 600 : 400 }
                  }}
                  helperText={!isEditMode && addEmployeeFormData.department ? 'Auto-generated — editable if needed' : ''}
                  FormHelperTextProps={{ sx: { color: '#2e7d32' } }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="First Name *" name="first_name" placeholder="First Name"
                  value={addEmployeeFormData.first_name} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Last Name *" name="last_name" placeholder="Last Name"
                  value={addEmployeeFormData.last_name} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email" name="email" type="email" placeholder="email@example.com"
                  value={addEmployeeFormData.email} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Username *" name="username" placeholder="Username"
                  value={addEmployeeFormData.username} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label={isEditMode ? 'Password (leave blank to keep current)' : 'Password *'} name="password" type="password" placeholder="Password"
                  value={addEmployeeFormData.password} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Phone *" name="phone" placeholder="Phone Number"
                  value={addEmployeeFormData.phone} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Role/Designation *" name="role" placeholder="e.g., Chef"
                  value={addEmployeeFormData.role} onChange={handleAddEmployeeFormChange} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} required />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Joining Date" name="joining_date" type="date"
                  value={addEmployeeFormData.joining_date} onChange={handleAddEmployeeFormChange} InputLabelProps={{ shrink: true }} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }} />
              </Grid>
            </Grid>

            {/* Photo Upload */}
            <Paper elevation={0} sx={{ backgroundColor: '#f5f5f5', border: '1px solid #e0e0e0', borderRadius: 2, p: 2 }}>
              <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>Photo</Typography>
              {selectedPhoto && !(selectedPhoto instanceof File) ? (
                <Box sx={{ mb: 1.5 }}>
                  <img src={selectedPhoto} alt="Current" style={{ maxHeight: 80, borderRadius: 8 }} />
                </Box>
              ) : selectedPhoto instanceof File ? (
                <Box sx={{ mb: 1.5 }}>
                  <img src={URL.createObjectURL(selectedPhoto)} alt="Preview" style={{ maxHeight: 80, borderRadius: 8 }} />
                </Box>
              ) : null}
              <Box sx={{ display: 'flex', gap: 1.5 }}>
                <Button variant="outlined" component="label" startIcon={<PhotoLibraryIcon />} fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#d0d0d0', flex: 1 }}>
                  Choose Photo
                  <input hidden accept="image/*" type="file" onChange={handlePhotoFileChange} />
                </Button>
                <Button variant="outlined" component="label" startIcon={<PhotoCameraIcon />} fullWidth
                  sx={{ borderRadius: 2, textTransform: 'none', borderColor: '#84cc16', color: '#2e7d32', flex: 1 }}>
                  Take Photo
                  <input hidden accept="image/*" type="file" capture="environment" onChange={handlePhotoFileChange} />
                </Button>
              </Box>
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
          <Button onClick={() => setAddEmployeeDialogOpen(false)} disabled={processing} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleSubmitAddEmployee} variant="contained" disabled={processing}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600, backgroundColor: '#84cc16', color: '#fff', '&:hover': { backgroundColor: '#65a30d' } }}>
            {processing ? <CircularProgress size={22} color="inherit" /> : (isEditMode ? 'Save Changes' : 'Add Employee')}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
};

export default EmployeeManagementPage;
